import type { QueryClient } from '@tanstack/react-query';
import pino from 'pino';
import { redirect } from 'react-router';

import { manager, overviewConstants, teamAdmin } from '../../../constants/appConstants';
import { getCurrenciesQuery } from '../../../queries/currenciesQuery';
import { getCompanyMetricsQuery, getManagerMetricsQuery, getTeamMetricsQuery } from '../../../queries/dashboardQuery';
import { selectedTeamIdQuery, teamQuery } from '../../../queries/teamQuery';
import { getVerifyUserAccessQuery, userQuery } from '../../../queries/userQuery';
import { isCompanyOrSupportAdmin, isManager, isTeamAdmin } from '../../../selectors/userSelectors';

const logger = pino();

type DashboardRouteParams = {
	teamId: string;
};

export const dashboardLoader = async ({
	queryClient,
	params,
}: {
	queryClient: QueryClient;
	params: DashboardRouteParams;
}) => {
	const teamDataQuery = teamQuery({ teamId: params.teamId });
	const currencies = await queryClient.ensureQueryData(getCurrenciesQuery(undefined));
	const selectedTeamId = await queryClient.ensureQueryData(selectedTeamIdQuery('selectedTeamID'));

	let hasCompanyAccess;
	try {
		const props = {
			objectId: 'company_settings',
			operationId: 'view',
			groupId: undefined,
			teamId: undefined,
			userId: undefined,
		};
		hasCompanyAccess = await queryClient.ensureQueryData(getVerifyUserAccessQuery(props));
	} catch (error) {
		logger.error({ error });
	}

	const user = await queryClient.ensureQueryData(userQuery);

	const userRole = isCompanyOrSupportAdmin(user.roles)
		? overviewConstants.COMPANY_ADMIN
		: user.roles.find(role => role.securityGroupId === String(selectedTeamId))?.roleId || '';

	let dashboardMetrics;
	let teamName;
	let initialTeam;

	if (isCompanyOrSupportAdmin(user.roles) && !params.teamId) {
		const companyMetricsQuery = getCompanyMetricsQuery();
		dashboardMetrics = await queryClient.ensureQueryData({
			queryKey: companyMetricsQuery.queryKey,
			queryFn: companyMetricsQuery.queryFn,
		});
	} else if ((isCompanyOrSupportAdmin(user.roles) && params.teamId) || userRole === teamAdmin) {
		const teamMetricsQuery = getTeamMetricsQuery(params.teamId || '');
		dashboardMetrics = await queryClient.ensureQueryData({
			queryKey: teamMetricsQuery.queryKey,
			queryFn: teamMetricsQuery.queryFn,
		});
		const team = await queryClient.ensureQueryData(teamDataQuery);
		initialTeam = team;
		teamName = team.name;
	} else if (userRole === manager) {
		const managerMetricsQuery = getManagerMetricsQuery(params.teamId || '');
		dashboardMetrics = await queryClient.ensureQueryData({
			queryKey: managerMetricsQuery.queryKey,
			queryFn: managerMetricsQuery.queryFn,
		});
		const team = await queryClient.ensureQueryData(teamDataQuery);
		teamName = team.name;
		initialTeam = team;
	} else {
		return redirect('/wallet/my-cards');
	}

	return {
		user,
		initialTeam,
		hasCompanyAccess,
		teamId: params.teamId,
		dashboardMetrics,
		teamName,
		currency: currencies?.[0] || 'USD',
		settlements: [],
		isCompanyDashboard: isCompanyOrSupportAdmin(user.roles) && !params.teamId,
		isCompanyTeamDashboard: (isCompanyOrSupportAdmin(user.roles) && params.teamId) || isTeamAdmin(user.roles),
		isManagerDashboard: isManager(user.roles),
	};
};
